import React, {useEffect, useMemo, useState} from 'react';
import {
    Box, CircularProgress, IconButton, Stack, TableSortLabel,
    Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Tooltip
} from '@mui/material';

import {
    deleteConversation
} from '../../services/DataService';
import {useAuth} from "../../context/useAuth";
import {RootState} from "../../store/store";
import {ConversationDto} from "../../dto/ConversationDto";
import axios from "axios";
import DeleteIcon from "@mui/icons-material/Delete";
import {setSnackbarOpen} from "../../store/slices/snackbar.slice";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {
    fetchConversationById,
    fetchConversations,
    setSelectedConversationId
} from "../../store/slices/conversations.slice";
import {MaiSplitPage} from "../../components/splitPage/MaiSplitPage";
import {sortConversations} from "../../utils/sortConversations";
import {TextFieldChat} from "../../components/textField/TextFieldChat";
import {Person, SmartToy} from "@mui/icons-material";
import theme from "../../theme/theme";


export const ChatAnalysisPage = () => {
    const dispatch = useAppDispatch();
    const {token, logout} = useAuth();
    const {selectedAgent} = useAppSelector((state: RootState) => state.agents);
    const { loadingAll, loadingConversation } = useAppSelector((state: RootState) => state.conversations);
    const conversations = useAppSelector((state: RootState) => {
        return selectedAgent !== null && selectedAgent !== undefined ? state.conversations.conversationsByAgentId[selectedAgent.gcpAgentId] || []: [];
    });
    const conversationId = useAppSelector((state) => state.conversations.selectedConversationId);
    const selectedConversation = useAppSelector((state) =>
        conversationId ? state.conversations.conversationByConversationId[conversationId] : null
    );
    const [sortBy, setSortBy] = useState<'id' | 'start' | 'duration' | 'turns' | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const handleRowClick = (conversation: ConversationDto) => {
        dispatch(setSelectedConversationId(conversation.conversationId));
    };

    const getConversations = () => {
        if (selectedAgent && token) {
            dispatch(fetchConversations({agentId: selectedAgent.gcpAgentId, token}));
        }
    }

    const handleDelete = async (conversationId: string) => {
        if (!selectedAgent || !token) return;
        try {
            setDeletingId(conversationId);
            await deleteConversation(selectedAgent.gcpAgentId, conversationId, token);
            setDeletingId(null);
            if (selectedConversation?.conversationId === conversationId) {
                dispatch(setSelectedConversationId(null));
            }
            dispatch(setSnackbarOpen({ snackbarMessage: "Löschen erfolgreich!", snackbarSeverity: 'success' }));
            getConversations();
        } catch (error) {
            if (axios.isAxiosError(error) && error.response?.status === 401) {
                logout();
            } else {
                console.error(error);
            }
        }
    };

    const sortedConversations = useMemo(
        () => sortConversations(conversations, sortBy, sortDirection),
        [conversations, sortBy, sortDirection]
    );

    const handleSort = (column: typeof sortBy) => {
        if (sortBy === column) {
            setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(column);
            setSortDirection('asc');
        }
    };

    const getSortLabelSx = (column: typeof sortBy) => ({
        color: sortBy === column ? 'primary.main' : 'text.primary',
        '&:hover': {
            color: 'primary.dark',
            '& .MuiTableSortLabel-icon': { color: 'primary.dark' },
        },
        '&.Mui-active': {
            color: 'primary.main',
            '& .MuiTableSortLabel-icon': { color: 'primary.main' },
        },
    });

    useEffect(() => {
        if (token && selectedAgent && conversations.length === 0) {
            dispatch(fetchConversations({agentId: selectedAgent.gcpAgentId, token}));
        }
    }, [dispatch, selectedAgent?.gcpAgentId, token, conversations.length]);

    useEffect(() => {
        if (selectedAgent && token && conversationId && (!selectedConversation || !selectedConversation.messages)) {
            dispatch(fetchConversationById({agentId: selectedAgent.gcpAgentId, conversationId, token}));
        }
    }, [conversationId, selectedAgent, token, selectedConversation]);

    const leftPage = (
        <Box sx={{ width: '100%', height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {loadingAll ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : conversations.length === 0 ? (
                <Box p={2}>
                    <Typography variant="explanation">Keine Sessions vorhanden.</Typography>
                </Box>
            ) : (
                <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sortDirection={sortBy === 'id' ? sortDirection : false}
                                    aria-sort={
                                        sortBy === 'id'
                                            ? sortDirection === 'asc'
                                                ? 'ascending'
                                                : 'descending'
                                            : undefined
                                    }
                                >
                                    <TableSortLabel
                                        active={sortBy === 'id'}
                                        direction={sortBy === 'id' ? sortDirection : 'asc'}
                                        sx={getSortLabelSx('id')}
                                        onClick={() => handleSort('id')}
                                    >
                                        <strong>Session ID</strong>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell
                                    sortDirection={sortBy === 'start' ? sortDirection : false}
                                    aria-sort={
                                        sortBy === 'start'
                                            ? sortDirection === 'asc'
                                                ? 'ascending'
                                                : 'descending'
                                            : undefined
                                    }
                                >
                                    <TableSortLabel
                                        active={sortBy === 'start'}
                                        direction={sortBy === 'start' ? sortDirection : 'asc'}
                                        sx={getSortLabelSx('start')}
                                        onClick={() => handleSort('start')}
                                    >
                                        <strong>Startzeitpunkt</strong>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell
                                    sortDirection={sortBy === 'duration' ? sortDirection : false}
                                    aria-sort={
                                        sortBy === 'duration'
                                            ? sortDirection === 'asc'
                                                ? 'ascending'
                                                : 'descending'
                                            : undefined
                                    }
                                >
                                    <TableSortLabel
                                        active={sortBy === 'duration'}
                                        direction={sortBy === 'duration' ? sortDirection : 'asc'}
                                        sx={getSortLabelSx('duration')}
                                        onClick={() => handleSort('duration')}
                                    >
                                        <strong>Dauer (in Sek.)</strong>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell
                                    sortDirection={sortBy === 'turns' ? sortDirection : false}
                                    aria-sort={
                                        sortBy === 'turns'
                                            ? sortDirection === 'asc'
                                                ? 'ascending'
                                                : 'descending'
                                            : undefined
                                    }
                                >
                                    <TableSortLabel
                                        active={sortBy === 'turns'}
                                        direction={sortBy === 'turns' ? sortDirection : 'asc'}
                                        sx={getSortLabelSx('turns')}
                                        onClick={() => handleSort('turns')}
                                    >
                                        <strong>Anzahl an Turns</strong>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <strong></strong>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedConversations.map((conversation) => (
                                <TableRow
                                    key={conversation.conversationId}
                                    hover
                                    selected={selectedConversation?.conversationId === conversation.conversationId}
                                    onClick={() => handleRowClick(conversation)}
                                    onKeyDown={(e) => {
                                        if (
                                            (e.key === 'Enter' || e.key === ' ') &&
                                            e.target === e.currentTarget
                                        ) {
                                            e.preventDefault();
                                            handleRowClick(conversation);
                                        }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    aria-pressed={selectedConversation?.conversationId === conversation.conversationId}
                                    sx={{
                                        cursor: 'pointer',
                                        '&:focus-visible': {
                                            outline: '2px solid',
                                            outlineColor: 'primary.main',
                                            outlineOffset: '-1px',
                                        },
                                        '&:hover': {
                                            backgroundColor: theme.palette.custom.customBackgroundGray,
                                        },
                                        '&.Mui-selected': {
                                            backgroundColor: theme.palette.custom.customBackgroundGray,
                                            '&:hover': {
                                                backgroundColor: theme.palette.custom.customBackgroundGray,
                                            },
                                        }
                                    }}
                                >
                                    <TableCell>{conversation.conversationId}</TableCell>
                                    <TableCell>{new Date(conversation.startTime).toLocaleString()}</TableCell>
                                    <TableCell>{conversation.duration}</TableCell>
                                    <TableCell>{conversation.interactionCount}</TableCell>
                                    <TableCell>
                                        <Tooltip title="Gespräch löschen">
                                            <IconButton
                                                aria-label="löschen"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDelete(conversation.conversationId);
                                                }}
                                                sx={{ color: theme.palette.secondary.main }}
                                            >
                                                {deletingId === conversation.conversationId ? (
                                                    <CircularProgress size={20} />
                                                ) : (
                                                    <DeleteIcon />
                                                )}
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    )

    const rightPage = (
        <Box
            bgcolor={theme.palette.custom.customBackgroundGray}
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}>
            {loadingConversation ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {conversationId && (
                        <Typography
                            variant="h6"
                            align="center"
                            bgcolor={theme.palette.custom.customBackgroundGray}
                            p={2}
                        >
                            {
                                selectedConversation
                                    ? `Chat am ${new Date(selectedConversation.startTime).toLocaleString()}, Session ID: ${selectedConversation.conversationId}`
                                    : 'Lade Chat...'
                            }
                        </Typography>
                    )}
                    <Stack spacing={2} sx={{p:2, bgcolor: theme.palette.custom.customBackgroundGray,}}>
                        {selectedConversation?.messages?.length ? (selectedConversation.messages.map((message, index) => (
                            <React.Fragment key={`${selectedConversation.conversationId}-${index}`}>
                                <TextFieldChat
                                    readOnly
                                    fullWidth
                                    value={message.request}
                                    startIcon={
                                        <Person color="secondary" fontSize="large"/>
                                    }
                                />
                                <TextFieldChat
                                    readOnly
                                    fullWidth
                                    value={message.response}
                                    startIcon={
                                        <SmartToy color="secondary" fontSize="large"/>
                                    }
                                />
                            </React.Fragment>
                        ))) : (
                            <Typography variant={'explanation'}>Keine Nachrichten vorhanden.</Typography>
                        )}
                    </Stack>
                </>
            )}
        </Box>
    )

    return (
        <MaiSplitPage
            childrenLeft={leftPage}
            childrenRight={rightPage}
        />
    );
};